import { TrendUpIcon } from "@phosphor-icons/react";
import { useState } from "react";
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type { DailyBookingStat } from "../ops-dashboard.hooks";
import { SectionCard } from "./SectionCard";

// Recharts writes fills/strokes as SVG attributes, where CSS var() doesn't
// resolve — so these are the literal brand hexes from packages/ui globals.css
// (vault BRANDING_v2.md, July 2026 rebrand). Keep in sync if the palette ever changes.
const COLOR_PRIMARY = "#00927a";
const COLOR_PRIMARY_LIGHT = "#29cba3";
const COLOR_GOLD = "#c9a84c";
const COLOR_TEXT_SECONDARY = "#6b6b6b";
const COLOR_BORDER = "#d4e5da";

const peso = (n: number) => `₱${n.toLocaleString("en-PH")}`;

const AXIS_STYLE = { fontSize: 11, fill: COLOR_TEXT_SECONDARY } as const;
const TOOLTIP_STYLE = {
  borderRadius: 8,
  border: `1px solid ${COLOR_BORDER}`,
  fontSize: 12,
} as const;

interface Booking {
  status: string;
  scheduled_at: string;
}

interface TrendsCardProps {
  weekly: DailyBookingStat[];
  /** Unfiltered hub bookings feed; only today's rows are used. */
  bookings: Booking[];
  chartHeight?: number;
}

type View = "weekly" | "today";

export function TrendsCard({ weekly, bookings, chartHeight = 240 }: TrendsCardProps) {
  const [view, setView] = useState<View>("weekly");

  // Today's load by hour: only the hours between the first and last booking
  // (±1) — operating hours are an Open Item, so we don't assume a window.
  const today = bookings.filter(
    (b) =>
      b.status !== "cancelled" &&
      new Date(b.scheduled_at).toDateString() === new Date().toDateString(),
  );
  const hours = today.map((b) => new Date(b.scheduled_at).getHours());
  const minHour = hours.length ? Math.max(0, Math.min(...hours) - 1) : 8;
  const maxHour = hours.length ? Math.min(23, Math.max(...hours) + 1) : 20;
  const hourly = [];
  for (let h = minHour; h <= maxHour; h++) {
    hourly.push({
      hour: `${((h + 11) % 12) + 1}${h < 12 ? "am" : "pm"}`,
      bookings: hours.filter((x) => x === h).length,
    });
  }

  const toggle = (
    <div className="flex rounded-full bg-background p-0.5">
      {(["weekly", "today"] as const).map((v) => (
        <button
          key={v}
          onClick={() => setView(v)}
          className={`rounded-full px-3 py-1 text-xs font-medium capitalize transition-colors ${
            view === v ? "bg-primary text-white" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {v}
        </button>
      ))}
    </div>
  );

  return (
    <SectionCard title="Trends" icon={TrendUpIcon} actions={toggle}>
      {view === "weekly" ? (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart data={weekly} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
            <CartesianGrid stroke={COLOR_BORDER} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="day" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <YAxis
              yAxisId="count"
              tick={AXIS_STYLE}
              tickLine={false}
              axisLine={false}
              allowDecimals={false}
            />
            <YAxis yAxisId="value" orientation="right" hide />
            <Tooltip
              contentStyle={TOOLTIP_STYLE}
              formatter={(value, name) =>
                name === "Booked value" ? [peso(Number(value)), name] : [value, name]
              }
            />
            <Bar
              yAxisId="count"
              dataKey="bookings"
              name="Bookings"
              fill={COLOR_PRIMARY}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
            />
            <Line
              yAxisId="value"
              dataKey="bookedValue"
              name="Booked value"
              stroke={COLOR_GOLD}
              strokeWidth={2}
              dot={{ r: 3, fill: COLOR_GOLD, strokeWidth: 0 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      ) : today.length === 0 ? (
        <div
          className="flex items-center justify-center rounded-lg border border-dashed border-border text-sm text-muted-foreground"
          style={{ height: chartHeight }}
        >
          No bookings scheduled today.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={chartHeight}>
          <ComposedChart data={hourly} margin={{ top: 4, right: 4, bottom: 0, left: -16 }}>
            <CartesianGrid stroke={COLOR_BORDER} strokeDasharray="3 3" vertical={false} />
            <XAxis dataKey="hour" tick={AXIS_STYLE} tickLine={false} axisLine={false} />
            <YAxis tick={AXIS_STYLE} tickLine={false} axisLine={false} allowDecimals={false} />
            <Tooltip contentStyle={TOOLTIP_STYLE} />
            <Bar
              dataKey="bookings"
              name="Bookings"
              fill={COLOR_PRIMARY_LIGHT}
              radius={[4, 4, 0, 0]}
              maxBarSize={28}
            />
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </SectionCard>
  );
}
